import { Box, Chip, CircularProgress, Paper, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import type { ReactNode } from "react";

import type { Dashboard, DraftProgress } from "../types";
import { SignedOffChip, signerName } from "./SignOff";
import { SummaryEditButton, SummaryHistoryButton } from "./SummaryEditing";

const Root = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.spacing(3),
  padding: theme.spacing(2, 2.5),
}));

const Identity = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  marginRight: "auto",
  minWidth: 0,
}));

const Field = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.spacing(0.25),
  minWidth: 92,
}));

/** A full-width row under the header's fields. */
const Below = styled(Box)(({ theme }) => ({
  flexBasis: "100%",
  minWidth: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  paddingTop: theme.spacing(1.5),
}));

export const Label = styled(Typography)(({ theme }) => ({
  ...theme.typography.overline,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
}));

const Value = styled(Typography)(({ theme }) => ({
  fontSize: "0.9375rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontVariantNumeric: "tabular-nums",
}));

export const Meta = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));

/** A draft's panel (the handoff). */
export const Summary = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.75),
}));

export const SummaryHead = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));

export const SummaryText = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  lineHeight: 1.55,
  color: theme.palette.text.primary,
  maxWidth: "110ch",
  // The summary is laid out in short sections (vitals by alarm limit, AI
  // situation); keep its line breaks and indentation.
  whiteSpace: "pre-wrap",
}));

/** The summary's and the handoff's one status chip while they are the AI's: signing them off comes once the
 *  mission has ended (SignOff). */
export const AI_DRAFT = "AI draft";

const PHASE_LABEL: Record<string, string> = {
  pending: "Pending",
  en_route: "En route",
  on_scene: "On scene",
  transporting: "Transport",
  complete: "Complete",
};

type MissionHeaderProps = {
  dashboard: Dashboard;
  /** Shown as a full-width row under the header's fields (the latest observations). */
  children?: ReactNode;
  /** Shown as a last full-width row, under the children (the mission summary). */
  footer?: ReactNode;
  live: boolean;
  /** Newest status the device reported over the socket; beats the stored one. */
  liveDeviceStatus?: string | null;
  liveStreaming?: boolean | null;
};

/** A newer draft on its way: the model drafts one at a time and takes minutes, so the shown draft (or none) can
 *  trail the mission for a while. */
/** "John Smith · 61 M", the patient id when no name was entered, or "—". */
function patientLabel(dashboard: Dashboard): string {
  const who = [dashboard.patient_age ?? null, dashboard.patient_sex ? dashboard.patient_sex[0].toUpperCase() : null]
    .filter((part) => part !== null)
    .join(" ");
  const name = dashboard.patient_name ?? dashboard.patient_id;
  return [name, who].filter(Boolean).join(" · ") || "—";
}

export function DraftProgressChip({ progress, hasDraft }: { progress?: DraftProgress; hasDraft: boolean }) {
  if (!progress) return null;
  const drafting = progress.state === "drafting";
  const label = drafting ? (hasDraft ? "Updating…" : "Drafting…") : hasDraft ? "Update queued" : "Queued";
  const title = drafting
    ? `The AI is writing ${hasDraft ? "a newer version" : "the first version"} (since ${formatAt(progress.since)}).`
    : `A ${hasDraft ? "newer version" : "first version"} is waiting for the AI (since ${formatAt(progress.since)}); it drafts one at a time.`;
  return (
    <Tooltip title={title}>
      <Chip
        size="small"
        variant="outlined"
        color="info"
        icon={drafting ? <CircularProgress size={12} color="inherit" /> : undefined}
        label={label}
      />
    </Tooltip>
  );
}

/** The empty text of a draft not drafted yet, or "" when none is on its way. */
export function pendingText(progress: DraftProgress | undefined, what: string): string {
  if (!progress) return "";
  return progress.state === "drafting"
    ? `The AI is drafting the first ${what}…`
    : `The first ${what} is queued for the AI, which drafts one at a time.`;
}

export function formatAt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Mission identity and operational state, plus the Sentio device behind it
 *  (what the old placeholder's "Active mission" and "Device status" cards showed). */
export function MissionHeader(props: MissionHeaderProps) {
  const { dashboard } = props;
  const device = dashboard.device;
  const deviceStatus = props.liveDeviceStatus ?? device?.device_status ?? null;
  const reachable = Boolean(deviceStatus) && deviceStatus !== "Unreachable";

  return (
    <Root>
      <Identity>
        <Label>Mission</Label>
        <Value noWrap title={dashboard.mission_id}>
          {device?.intervention_id ?? dashboard.mission_id}
        </Value>
        {device ? (
          <Meta noWrap>
            {[device.nickname, device.device_type, device.device_id].filter(Boolean).join(" · ")}
          </Meta>
        ) : null}
      </Identity>

      <Field>
        <Label>Phase</Label>
        <Chip size="small" color="primary" variant="outlined" label={PHASE_LABEL[dashboard.phase] ?? dashboard.phase} />
      </Field>

      <Field>
        <Label>ETA</Label>
        <Value>{dashboard.eta_minutes === null ? "—" : `${dashboard.eta_minutes} min`}</Value>
      </Field>

      <Field>
        <Label>Destination</Label>
        <Value>{dashboard.destination ?? "—"}</Value>
      </Field>

      <Field>
        <Label>Patient</Label>
        <Value>{patientLabel(dashboard)}</Value>
      </Field>

      {device ? (
        <Field>
          <Label>Device</Label>
          <Chip
            size="small"
            color={reachable ? "success" : "default"}
            variant={reachable ? "filled" : "outlined"}
            label={
              (deviceStatus ?? "unknown") + (props.liveStreaming ? " · streaming" : "")
            }
          />
        </Field>
      ) : null}

      <Field>
        <Label>State</Label>
        <Value>v{dashboard.state_version}</Value>
      </Field>

      <Field>
        <Label>Updates</Label>
        {/* The page's connection is per device and stays open after End Mission (late drafts, sign-offs, the
            ePCR still arrive), so an ended mission says so rather than "Live". */}
        <Tooltip
          title={
            !props.live
              ? "The page lost its live connection: changes show once it reconnects."
              : dashboard.ended_at
                ? "The mission has ended. The page still updates as drafts finish, sign-offs and edits are made, and the ePCR is ready."
                : "The page updates as the device and crew record."
          }
          describeChild
        >
          <Chip
            size="small"
            color={props.live && !dashboard.ended_at ? "success" : "default"}
            variant={props.live && !dashboard.ended_at ? "filled" : "outlined"}
            label={!props.live ? "Reconnecting" : dashboard.ended_at ? "Mission ended" : "Live"}
          />
        </Tooltip>
      </Field>

      {props.children ? <Below>{props.children}</Below> : null}
      {props.footer ? <Below>{props.footer}</Below> : null}
    </Root>
  );
}

/** A small card inside the mission header. */
const Note = styled(Box)(({ theme }) => ({
  background: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.25, 1.75),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.75),
}));

/** One labelled line of the note: its label in a fixed column, so the two
 *  texts start at the same place. */
const NoteRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(0, 11rem) minmax(0, 1fr)",
  columnGap: theme.spacing(1.5),
  alignItems: "baseline",
  [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0, 1fr)" },
}));

/** The mission summary, cut to what only it has: the situation the model
 *  drafted (or a person edited, once the mission ended), marked as AI or as
 *  the editor's. The rest of the summary (vitals by alarm limit,
 *  interventions, destination) is what this header and the page already
 *  show, so it is not repeated. */
export function MissionSummaryNote({ dashboard, onChanged }: { dashboard: Dashboard; onChanged: () => void }) {
  const summary = dashboard.mission_summary;
  const edited = summary?.source === "manual";
  const signed = summary?.signed_off;
  const signedShown = Boolean(signed && signed.version === summary?.version);
  const empty = !summary?.situation;
  // An edited or signed-off summary is final for the AI: it drafts it no more, so a queued draft is not coming.
  const progress = edited || signed ? undefined : dashboard.drafting?.mission_summary;
  // Who wrote the situation, and its sign-off: under its label (or in the head when there is none).
  const status = summary ? (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, minWidth: 0 }}>
      {edited ? (
        <Meta sx={{ overflowWrap: "anywhere" }}>Edited by {summary.authored_by ?? "unknown"}</Meta>
      ) : (
        <Chip size="small" color="warning" variant="outlined" label={AI_DRAFT} />
      )}
      {signed && signedShown ? <SignedOffChip signed={signed} /> : null}
      {signed && !signedShown ? (
        <Meta sx={{ overflowWrap: "anywhere" }}>
          Not signed off since this edit · v{signed.version} signed off by {signerName(signed)}, {formatAt(signed.at)}
        </Meta>
      ) : null}
    </Box>
  ) : null;

  return (
    <Note>
      <SummaryHead>
        <Label>Mission summary</Label>
        {summary ? (
          <>
            {empty ? status : null}
            <Meta>
              v{summary.version} · {formatAt(summary.created_at)}
            </Meta>
          </>
        ) : null}
        <DraftProgressChip progress={progress} hasDraft={Boolean(summary)} />
        {summary ? (
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            <SummaryHistoryButton missionId={dashboard.mission_id} summary={summary} onChanged={onChanged} />
            {summary.editable ? (
              <SummaryEditButton
                missionId={dashboard.mission_id}
                summary={summary}
                drafting={Boolean(dashboard.drafting?.mission_summary) && !edited && !signed}
                onChanged={onChanged}
              />
            ) : null}
          </Box>
        ) : null}
      </SummaryHead>
      {summary?.situation ? (
        <NoteRow>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
            <Meta>Situation</Meta>
            {status}
          </Box>
          <SummaryText>{summary.situation}</SummaryText>
        </NoteRow>
      ) : null}
      {empty ? (
        <SummaryText sx={{ color: "text.secondary" }}>
          {summary
            ? "No situation recorded yet."
            : pendingText(progress, "summary") || "No summary yet. One is drafted as the mission records facts."}
        </SummaryText>
      ) : null}
    </Note>
  );
}
